// TanStack Query hooks for the Daily Activity Reports (DAR) module. A DAR is a
// per-shift log filed by an officer (assignment + site + date + summary) that
// moves through the same approval lifecycle as incidents (draft → submitted →
// under_review → approved | rejected). Each DAR owns a soft-deletable list of
// timestamped activity entries. Mirrors packages/api/src/routes/dars.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDarEntryInput,
  CreateDarInput,
  DarEntriesListResponse,
  DarEntryResponse,
  DarResponse,
  DarsListResponse,
  DarTransitionInput,
  ReportStatus,
  UpdateDarEntryInput,
  UpdateDarInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'dars';

export const darKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: DarFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  entries: (id: number) => [KEY, 'detail', id, 'entries'] as const,
};

// =============================================================================
// Daily activity reports
// =============================================================================

export interface DarFilters {
  status?: ReportStatus;
  assignment_id?: number;
  site_id?: number;
  personnel_id?: number;
}

export function useDars(filters: DarFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.assignment_id !== undefined) {
    params.set('assignment_id', String(filters.assignment_id));
  }
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.personnel_id !== undefined) {
    params.set('personnel_id', String(filters.personnel_id));
  }
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/dars?${qs}` : '/api/dars';
  return useQuery({
    queryKey: darKeys.list(filters),
    queryFn: ({ signal }) => api.get<DarsListResponse>(url, signal),
  });
}

export function useDar(id: number | null) {
  return useQuery({
    queryKey: darKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<DarResponse>(`/api/dars/${id}`, signal),
  });
}

export function useCreateDar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDarInput) => api.post<DarResponse>('/api/dars', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: darKeys.lists() });
    },
  });
}

export interface UpdateDarArgs extends UpdateDarInput {
  id: number;
}

export function useUpdateDar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateDarArgs) =>
      api.patch<DarResponse>(`/api/dars/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: darKeys.lists() });
      void qc.invalidateQueries({ queryKey: darKeys.detail(id) });
    },
  });
}

/**
 * Move a DAR through its approval lifecycle. Gated server-side on dar.review
 * (distinct from dar.write).
 */
export interface TransitionDarArgs extends DarTransitionInput {
  id: number;
}

export function useTransitionDar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: TransitionDarArgs) =>
      api.post<DarResponse>(`/api/dars/${id}/transition`, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: darKeys.lists() });
      void qc.invalidateQueries({ queryKey: darKeys.detail(id) });
    },
  });
}

export function useDeleteDar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/dars/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: darKeys.lists() });
    },
  });
}

// =============================================================================
// DAR entries
// =============================================================================

export function useDarEntries(darId: number | null) {
  return useQuery({
    queryKey: darKeys.entries(darId ?? 0),
    enabled: darId !== null && darId > 0,
    queryFn: ({ signal }) =>
      api.get<DarEntriesListResponse>(`/api/dars/${darId}/entries`, signal),
  });
}

export interface CreateDarEntryArgs extends CreateDarEntryInput {
  darId: number;
}

export function useCreateDarEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ darId, ...input }: CreateDarEntryArgs) =>
      api.post<DarEntryResponse>(`/api/dars/${darId}/entries`, input),
    onSuccess: (_data, { darId }) => {
      void qc.invalidateQueries({ queryKey: darKeys.entries(darId) });
    },
  });
}

export interface UpdateDarEntryArgs extends UpdateDarEntryInput {
  darId: number;
  entryId: number;
}

export function useUpdateDarEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ darId, entryId, ...patch }: UpdateDarEntryArgs) =>
      api.patch<DarEntryResponse>(`/api/dars/${darId}/entries/${entryId}`, patch),
    onSuccess: (_data, { darId }) => {
      void qc.invalidateQueries({ queryKey: darKeys.entries(darId) });
    },
  });
}

export interface DeleteDarEntryArgs {
  darId: number;
  entryId: number;
}

export function useDeleteDarEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ darId, entryId }: DeleteDarEntryArgs) =>
      api.delete<void>(`/api/dars/${darId}/entries/${entryId}`),
    onSuccess: (_data, { darId }) => {
      void qc.invalidateQueries({ queryKey: darKeys.entries(darId) });
    },
  });
}
